import { Utils } from '../utils/Utils.js';

export class WebRTCManager extends EventTarget {
    constructor(signalingClient) {
        super();
        this.signalingClient = signalingClient;
        this.peerConnection = null;
        this.localStream = null;
        this.remoteStream = null;
        this.isVideoEnabled = true;
        this.isAudioEnabled = true;
        this.hasVideo = true;

        // Enhanced ICE servers configuration with fallbacks
        this.iceServers = [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
            { urls: 'stun:stun2.l.google.com:19302' },
            { urls: 'stun:stun3.l.google.com:19302' },
            { urls: 'stun:stun4.l.google.com:19302' }
        ];

        this.setupSignalingListeners();
    }

    async init(hasVideo = true) {
        try {
            this.hasVideo = hasVideo;

            // Get user media with mobile-optimized constraints
            await this.getUserMedia();

            // Create peer connection
            this.createPeerConnection();

            // Add local stream to peer connection
            if (this.localStream) {
                this.localStream.getTracks().forEach(track => {
                    this.peerConnection.addTrack(track, this.localStream);
                });
            }

            this.dispatchEvent(new CustomEvent('localStream', { detail: this.localStream }));

        } catch (error) {
            Utils.error('Failed to initialize WebRTC:', error);
            this.dispatchEvent(new CustomEvent('error', { detail: error }));
            throw error;
        }
    }

    async getUserMedia() {
        try {
            const constraints = {
                audio: Utils.getMobileAudioConstraints(),
                video: this.hasVideo ? Utils.getMobileVideoConstraints() : false
            };

            Utils.debug('Requesting media with constraints:', constraints);
            this.localStream = await navigator.mediaDevices.getUserMedia(constraints);

        } catch (error) {
            Utils.error('Failed to get user media:', error);

            // Try fallback constraints for mobile
            try {
                const fallbackConstraints = {
                    audio: {
                        echoCancellation: true,
                        noiseSuppression: true
                    },
                    video: this.hasVideo ? {
                        facingMode: 'user',
                        width: { ideal: 320, max: 640 },
                        height: { ideal: 240, max: 480 }
                    } : false
                };

                Utils.debug('Trying fallback constraints:', fallbackConstraints);
                this.localStream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);

            } catch (fallbackError) {
                Utils.error('Fallback getUserMedia failed:', fallbackError);

                // Final fallback - audio only
                try {
                    Utils.info('Trying audio-only fallback');
                    this.localStream = await navigator.mediaDevices.getUserMedia({
                        audio: true,
                        video: false
                    });
                    this.hasVideo = false;
                    this.dispatchEvent(new CustomEvent('videoUnavailable'));
                } catch (audioError) {
                    Utils.error('Audio-only fallback failed:', audioError);
                    throw new Error('Camera/microphone access denied or not available');
                }
            }
        }
    }

    createPeerConnection() {
        const config = {
            iceServers: this.iceServers,
            iceCandidatePoolSize: 10,
            // Mobile-specific optimizations
            bundlePolicy: 'max-bundle',
            rtcpMuxPolicy: 'require',
            // Better connection handling for mobile networks
            iceTransportPolicy: 'all',
            // Enable ICE restart for better mobile connectivity
            iceRestart: true
        };

        this.peerConnection = new RTCPeerConnection(config);

        // Handle ICE candidates
        this.peerConnection.onicecandidate = (event) => {
            if (event.candidate) {
                this.signalingClient.sendIceCandidate(event.candidate);
            }
        };

        // Handle remote stream
        this.peerConnection.ontrack = (event) => {
            Utils.debug('Received remote track:', event.track.kind);

            if (!this.remoteStream) {
                this.remoteStream = new MediaStream();
                this.dispatchEvent(new CustomEvent('remoteStream', { detail: this.remoteStream }));
            }

            this.remoteStream.addTrack(event.track);
        };

        // Handle connection state changes
        this.peerConnection.onconnectionstatechange = () => {
            const state = this.peerConnection.connectionState;
            Utils.debug('Connection state changed:', state);
            this.dispatchEvent(new CustomEvent('connectionStateChange', { detail: state }));

            if (state === 'disconnected' || state === 'failed') {
                this.dispatchEvent(new CustomEvent('peerDisconnected'));
            }
        };

        // Handle ICE connection state changes
        this.peerConnection.oniceconnectionstatechange = () => {
            const state = this.peerConnection.iceConnectionState;
            Utils.debug('ICE connection state:', state);

            if (state === 'failed') {
                // Try ICE restart
                this.restartIce();
            } else if (state === 'disconnected') {
                // Wait a bit before considering it failed
                setTimeout(() => {
                    if (this.peerConnection?.iceConnectionState === 'disconnected') {
                        this.restartIce();
                    }
                }, 3000);
            }
        };

        // Handle ICE gathering state
        this.peerConnection.onicegatheringstatechange = () => {
            const state = this.peerConnection.iceGatheringState;
            Utils.debug('ICE gathering state:', state);
        };

        // Handle signaling state
        this.peerConnection.onsignalingstatechange = () => {
            const state = this.peerConnection.signalingState;
            Utils.debug('Signaling state:', state);
        };
    }

    setupSignalingListeners() {
        this.signalingClient.addEventListener('offer', async (event) => {
            try {
                await this.handleOffer(event.detail);
            } catch (error) {
                Utils.error('Failed to handle offer:', error);
                this.dispatchEvent(new CustomEvent('error', { detail: error }));
            }
        });

        this.signalingClient.addEventListener('answer', async (event) => {
            try {
                await this.handleAnswer(event.detail);
            } catch (error) {
                Utils.error('Failed to handle answer:', error);
                this.dispatchEvent(new CustomEvent('error', { detail: error }));
            }
        });

        this.signalingClient.addEventListener('iceCandidate', async (event) => {
            try {
                await this.handleIceCandidate(event.detail);
            } catch (error) {
                Utils.error('Failed to handle ICE candidate:', error);
            }
        });

        this.signalingClient.addEventListener('peerJoined', async (_event) => {
            try {
                await this.createOffer();
            } catch (error) {
                Utils.error('Failed to create offer:', error);
                this.dispatchEvent(new CustomEvent('error', { detail: error }));
            }
        });

        // Listen for mock connection establishment (for testing)
        this.signalingClient.addEventListener('mockConnectionEstablished', () => {
            // Simulate successful connection for testing
            this.dispatchEvent(new CustomEvent('connectionStateChange', { detail: 'connected' }));
        });
    }

    async createOffer() {
        if (!this.peerConnection) return;

        try {
            const offer = await this.peerConnection.createOffer({
                offerToReceiveAudio: true,
                offerToReceiveVideo: this.hasVideo
            });

            await this.peerConnection.setLocalDescription(offer);
            this.signalingClient.sendOffer(offer);

        } catch (error) {
            Utils.error('Failed to create offer:', error);
            throw error;
        }
    }

    async handleOffer(offer) {
        if (!this.peerConnection) return;

        try {
            await this.peerConnection.setRemoteDescription(new RTCSessionDescription(offer));

            const answer = await this.peerConnection.createAnswer();
            await this.peerConnection.setLocalDescription(answer);

            this.signalingClient.sendAnswer(answer);

        } catch (error) {
            Utils.error('Failed to handle offer:', error);
            throw error;
        }
    }

    async handleAnswer(answer) {
        if (!this.peerConnection) return;

        try {
            await this.peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
        } catch (error) {
            Utils.error('Failed to handle answer:', error);
            throw error;
        }
    }

    async handleIceCandidate(candidate) {
        if (!this.peerConnection) return;

        try {
            await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (error) {
            Utils.error('Failed to add ICE candidate:', error);
        }
    }

    toggleVideo() {
        if (!this.localStream) return false;

        const videoTrack = this.localStream.getVideoTracks()[0];
        if (videoTrack) {
            videoTrack.enabled = !videoTrack.enabled;
            this.isVideoEnabled = videoTrack.enabled;
        }

        return this.isVideoEnabled;
    }

    toggleAudio() {
        if (!this.localStream) return false;

        const audioTrack = this.localStream.getAudioTracks()[0];
        if (audioTrack) {
            audioTrack.enabled = !audioTrack.enabled;
            this.isAudioEnabled = audioTrack.enabled;
        }

        return this.isAudioEnabled;
    }

    async restartIce() {
        if (!this.peerConnection) return;

        try {
            const offer = await this.peerConnection.createOffer({ iceRestart: true });
            await this.peerConnection.setLocalDescription(offer);
            this.signalingClient.sendOffer(offer);
        } catch (error) {
            Utils.error('Failed to restart ICE:', error);
        }
    }

    cleanup() {
        // Stop all local media tracks
        if (this.localStream) {
            this.localStream.getTracks().forEach(track => {
                track.stop();
                Utils.debug('Stopped local track:', track.kind);
            });
            this.localStream = null;
        }

        // Close peer connection
        if (this.peerConnection) {
            // Close all data channels
            if (this.peerConnection.getDataChannel) {
                try {
                    const dataChannel = this.peerConnection.getDataChannel('chat');
                    if (dataChannel) {
                        dataChannel.close();
                    }
                } catch (error) {
                    Utils.debug('No data channel to close:', error);
                }
            }

            // Close the peer connection
            this.peerConnection.close();
            this.peerConnection = null;
            Utils.debug('Peer connection closed');
        }

        // Clear remote stream
        if (this.remoteStream) {
            this.remoteStream.getTracks().forEach(track => {
                track.stop();
                Utils.debug('Stopped remote track:', track.kind);
            });
            this.remoteStream = null;
        }

        // Reset state
        this.hasVideo = true;
        this.isInitialized = false;
        this.isVideoEnabled = true;
        this.isAudioEnabled = true;
    }

    // Event helper methods
    on(event, callback) {
        this.addEventListener(event, (e) => callback(e.detail));
    }

    off(event, callback) {
        this.removeEventListener(event, callback);
    }
}
